import { Link, NavLink, Tag } from "@liberetech/design-system";
import classNames from "classnames";
import { useRouter } from "next/router";
import React from "react";

import CardKlee from "components/CardKlee/CardKlee";
import CardKleeActions from "components/CardKlee/CardKleeActions";
import CardKleeBanner from "components/CardKlee/CardKleeBanner";
import CardKleeFeaturedImage from "components/CardKlee/CardKleeFeaturedImage";
import CardKleeProse from "components/CardKlee/CardKleeProse";
import { useBrand } from "components/Context/BrandContext";
import {
  useFeatureIsOn,
  useFeaturesReady,
} from "components/Context/ExperimentContext";
import { useSearchBar } from "components/Context/PageContext";
import { useSession } from "components/Context/SessionContext";
import NextLinkButton from "components/NextLinkButton/NextLinkButton";
import SectionWithPromo from "components/SectionWithPromo/SectionWithPromo";
import { disallowedVoucherCodesForFeedback } from "lib/book/book";
import { Trans, useTranslation } from "lib/i18n/client";
import {
  isAssetPromotionEnabled,
  Promotion,
  promotionForAsset,
} from "lib/promotion/promotion";
import PricePerNight, {
  PricePerNightSkeleton,
} from "pagesComponents/_shared/PricePerNight/PricePerNight";

import {
  DestinationSectionAssetsAssetQuery,
  DestinationSectionAssetsAvailableRateFragment,
  useDestinationSectionAssetsAssetQuery,
} from "./DestinationSectionAssets.generated";
import styles from "./DestinationSectionAssets.module.css";

const DestinationSectionAssets: React.FC<DestinationSectionAssetsProps> = ({
  title,
  destination,
  isViewerInCommunity,
}) => {
  const { t } = useTranslation("destination");
  const brand = useBrand();
  const searchBar = useSearchBar();
  const assets = destination.assets;
  const promotion = assets.reduce(
    (_, asset) => promotionForAsset(asset.properties.id, brand),
    null as Promotion | null,
  );

  const onSearchOtherDatesClicked = () => {
    searchBar.open();
    window.dataLayer?.push({
      event: "click",
      event_category: "price_per_night",
      event_label: "change_dates",
    });
  };

  return (
    <SectionWithPromo
      id="assets"
      background="contrast"
      label={title}
      title={t("assets.title", { city: title })}
      description={
        assets.length > 0
          ? undefined
          : t("assets.comingSoon", {
              brand: brand.name,
              city: destination.title,
            })
      }
      promotion={promotion}
      brand={brand}
    >
      {assets.length > 0 && (
        <ul className={classNames(styles.assets, "grid")}>
          {assets.map((asset, i) => (
            <li className={styles.asset} key={i}>
              <DestinationSectionAssetsAsset
                asset={asset}
                isViewerInCommunity={isViewerInCommunity}
                onSearchOtherDatesClicked={onSearchOtherDatesClicked}
              />
            </li>
          ))}
        </ul>
      )}
    </SectionWithPromo>
  );
};

const DestinationSectionAssetsAsset: React.FC<{
  isViewerInCommunity: boolean;
  onSearchOtherDatesClicked: () => void;
  asset: {
    fullName: string;
    properties: { id: string; preview: string };
    uri: string;
    featuredImage: {
      node: {
        srcSet: string;
      };
    };
  };
}> = ({ asset, isViewerInCommunity, onSearchOtherDatesClicked }) => {
  const { t } = useTranslation("destination");
  const { query } = useRouter();
  const brand = useBrand();
  const session = useSession();

  const {
    data,
    isLoading: isLoadingGQL,
    isFetching: isFetchingGQL,
  } = useDestinationSectionAssetsAssetQuery(
    {
      assetId: asset.properties.id,
      checkinDate: query.from as string,
      checkoutDate: query.to as string,
      voucherCode: (query.voucher as string) ?? undefined,
    },
    { enabled: !!query.from && !!query.to },
  );
  const minRate = findCheapestAssetRate(data);
  const isLoading = isLoadingGQL && isFetchingGQL;
  const hasNoAvailability = !!(
    !isLoading &&
    query.from &&
    query.to &&
    !minRate
  );
  const voucherCodeUsed = query.voucher as string | undefined;
  const isCommunityApplicable =
    brand.id === "libere" &&
    (!session.session?.accountId ||
      session.session?.accountType === "INHABITANT");
  const isVoucherCodeNotApplied = !!(
    !isLoading &&
    !hasNoAvailability &&
    voucherCodeUsed &&
    !disallowedVoucherCodesForFeedback.includes(
      voucherCodeUsed.toLocaleUpperCase(),
    ) &&
    !minRate?.enabledByVoucherCode
  );

  const assetHref = `${asset.uri}${
    query.from && query.to
      ? `?${new URLSearchParams({
          from: (query.from || "") as string,
          to: (query.to || "") as string,
          voucher: (query.voucher || "") as string,
        }).toString()}#spaces`
      : ""
  }`;

  const mostRelevantMessageKey = mostRelevantMessage(
    hasNoAvailability,
    isViewerInCommunity,
    isVoucherCodeNotApplied,
    isCommunityApplicable,
  );

  const crossOutPricesExperimentEnabled = useFeatureIsOn(
    "show-direct-channel-discount",
  );
  const isFeaturesReady = useFeaturesReady();

  return (
    <CardKlee className={styles.assetKlee}>
      <CardKleeFeaturedImage
        label={
          isAssetPromotionEnabled(asset.properties.id, brand) && (
            <Tag type="contrast">{t("common:promo.discount")}</Tag>
          )
        }
      >
        <img
          srcSet={asset.featuredImage.node.srcSet}
          sizes="(min-width: 1024px) 450px, (min-width: 768px) 50vw, 100vw"
          loading="lazy"
        />
      </CardKleeFeaturedImage>
      <CardKleeProse className={styles.assetKleeContent}>
        <h3 className={styles.assetKleeTitle}>{asset.fullName}</h3>
        <div
          dangerouslySetInnerHTML={{
            __html: asset.properties.preview,
          }}
        />
      </CardKleeProse>
      <CardKleeActions noPadding>
        {mostRelevantMessageKey === "no_availability" && (
          <CardKleeBanner type="error">
            <Trans
              i18nKey="price:notAvailableOtherDates"
              components={{
                strong: <strong />,
                linkDates: (
                  <OpenSearchBarLink onClick={onSearchOtherDatesClicked} />
                ),
              }}
            />
          </CardKleeBanner>
        )}
        {mostRelevantMessageKey === "voucher_not_applied" && (
          <CardKleeBanner type={"warn"}>
            <Trans
              i18nKey={`price:voucherCodeNotApplied`}
              values={{ voucherCode: voucherCodeUsed }}
              components={{
                strong: <strong />,
                linkDates: onSearchOtherDatesClicked ? (
                  <OpenSearchBarLink onClick={onSearchOtherDatesClicked} />
                ) : (
                  <></>
                ),
              }}
            />
          </CardKleeBanner>
        )}
        {mostRelevantMessageKey === "viewer_not_in_community" && (
          <div className={styles.joinCommunity}>
            <Trans
              i18nKey={`communityJoin:joinCommunity${crossOutPricesExperimentEnabled && isFeaturesReady ? "_crossOutPricesExperiment" : ""}`}
              components={{
                strong: <strong />,
                communityLink: <CommunityLink />,
              }}
            />
          </div>
        )}
        <div className={styles.assetKleeFooter}>
          {isLoading && <PricePerNightSkeleton />}
          {minRate?.perNightPrice && !hasNoAvailability && (
            <PricePerNight
              minPrice={minRate.perNightPrice}
              originalPrice={minRate.originalPerNightPrice || undefined}
              promoName={minRate.promoName}
            />
          )}
          <NextLinkButton href={assetHref} locale={false}>
            {hasNoAvailability
              ? t("assets.seeAccomodation")
              : t("assets.checkAvailability")}
          </NextLinkButton>
        </div>
      </CardKleeActions>
    </CardKlee>
  );
};

const mostRelevantMessage = (
  hasNoAvailability: boolean,
  isViewerInCommunity: boolean,
  isVoucherCodeNotApplied: boolean,
  isCommunityApplicable: boolean,
) => {
  return hasNoAvailability
    ? "no_availability"
    : isVoucherCodeNotApplied
      ? "voucher_not_applied"
      : !isViewerInCommunity && isCommunityApplicable
        ? "viewer_not_in_community"
        : "";
};

const findCheapestAssetRate = (
  data: DestinationSectionAssetsAssetQuery | undefined,
) => {
  if (
    !data?.asset?.spaceCategories ||
    data.asset.spaceCategories.length === 0
  ) {
    return null;
  }

  return data.asset.spaceCategories
    .reduce((prev, category) => {
      if (!category.availableRates?.nodes) {
        return prev;
      }

      return [...prev, ...category.availableRates.nodes.map((rate) => rate)];
    }, [] as DestinationSectionAssetsAvailableRateFragment[])
    .reduce(
      (prev, rate) => {
        if (!prev) {
          return rate;
        }
        return prev.perNightPrice.amount < rate.perNightPrice.amount
          ? prev
          : rate;
      },
      null as DestinationSectionAssetsAvailableRateFragment | null,
    );
};

const CommunityLink: React.FC = ({ children }) => {
  return (
    <NavLink href="/community">
      <Link>{children}</Link>
    </NavLink>
  );
};

const OpenSearchBarLink: React.FC<{
  onClick: () => void;
}> = ({ children, onClick }) => {
  return (
    <button onClick={onClick}>
      <Link>{children}</Link>
    </button>
  );
};

type DestinationSectionAssetsProps = {
  title: string;
  isViewerInCommunity: boolean;
  destination: {
    title: string;
    assets: {
      fullName: string;
      properties: { id: string; preview: string };
      uri: string;
      featuredImage: {
        node: {
          srcSet: string;
        };
      };
    }[];
  };
};

export default DestinationSectionAssets;
